import React, { useRef, useEffect, useState, useCallback } from 'react';
import './VideoSection.css';

// Importez votre vidéo
import visionProVideo from '../assets/videos/demo-video.mp4';

// --- Icônes SVG ---
const PlayIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 5.14v13.72L19 12L8 5.14z" fill="white"/>
  </svg>
);

const PauseIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="7" y="5" width="3" height="14" rx="1" fill="white"/>
    <rect x="14" y="5" width="3" height="14" rx="1" fill="white"/>
  </svg>
);

const VideoSection = () => {
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false); 
  const [progress, setProgress] = useState(0);

  // Fonction principale pour basculer la lecture/pause
  const togglePlayPause = useCallback(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    setUserInteracted(true); 

    if (isPlaying) {
      videoElement.pause();
      setIsPlaying(false);
    } else {
      if (videoElement.muted) {
        videoElement.muted = false;
      }
      videoElement.play().catch(error => {
        console.error("Erreur de lecture de la vidéo:", error);
      });
      setIsPlaying(true);
    }
  }, [isPlaying]);

  useEffect(() => {
    const videoElement = videoRef.current;
    const sectionElement = sectionRef.current;
    if (!videoElement) return;

    // --- Gestion du Scroll (Autoplay) ---
    const handleScroll = () => {
      if (!sectionElement) return;

      const rect = sectionElement.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const isVisible = rect.top < windowHeight && rect.bottom > 0;
      
      const visiblePercentage = Math.min(
        Math.max(
          ((windowHeight - rect.top) / (windowHeight + rect.height)) * 100,
          0
        ),
        100
      );

      // Autoplay si visible et pas d'interaction manuelle
      if (visiblePercentage > 30 && !isPlaying && !userInteracted) {
        videoElement.play().then(() => {
          setIsPlaying(true);
        }).catch(error => {
          console.log('Autoplay prevented:', error);
        });
      }

      // Pause si non visible
      if (!isVisible && isPlaying) {
        videoElement.pause();
        setIsPlaying(false);
      }
    };
    
    // --- Mise à jour de la progression ---
    const onTimeUpdate = () => {
      if (videoElement.duration) {
        const progressPercent = (videoElement.currentTime / videoElement.duration) * 100;
        setProgress(progressPercent);
      }
    };
    
    const onVideoPlay = () => setIsPlaying(true);
    const onVideoPause = () => setIsPlaying(false);

    // Ajout des écouteurs
    videoElement.addEventListener('timeupdate', onTimeUpdate);
    videoElement.addEventListener('play', onVideoPlay);
    videoElement.addEventListener('pause', onVideoPause);
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); 

    return () => {
      // Nettoyage des écouteurs
      videoElement.removeEventListener('timeupdate', onTimeUpdate);
      videoElement.removeEventListener('play', onVideoPlay);
      videoElement.removeEventListener('pause', onVideoPause);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isPlaying, userInteracted]);

  return (
    <section 
      className="video-section" 
      id="demo" 
      ref={sectionRef}
      data-paused={!isPlaying ? "true" : "false"}
      onClick={togglePlayPause}
    >
      <div className="video-aspect-ratio-wrapper">
        <video
          ref={videoRef}
          className="video-player"
          loop
          muted
          playsInline
          preload="metadata"
        >
          <source src={visionProVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* OVERLAY AVEC CERCLE DE PROGRESSION */}
        <div className="video-overlay">
          <button 
            className="video-control-btn"
            onClick={(e) => {
              e.stopPropagation();
              togglePlayPause();
            }}
            aria-label={isPlaying ? "Mettre en pause" : "Lire la vidéo"}
            style={{
              '--progress': `${progress}%`
            }}
          >
            <svg className="progress-ring" viewBox="0 0 100 100">
              <circle 
                className="progress-ring-bg" 
                cx="50" 
                cy="50" 
                r="46"
              />
              <circle 
                className="progress-ring-progress" 
                cx="50" 
                cy="50" 
                r="46"
                style={{
                  strokeDashoffset: `${289 - (289 * progress) / 100}`
                }}
              />
            </svg>
            <div className="video-icon">
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </div>
          </button>
        </div>
      </div>
    </section>
  );
};

export default VideoSection;